/**
 * ヘッダー画像の効果（PRO限定）。
 *
 * CSS の transform / opacity だけで動かす。**JSは走らせない。**
 *
 * 【WebGLで歪ませる案は撤去済み。復活させないこと】
 * ピクセル単位の歪みはシェーダーでしか書けないが、1120x420 を毎フレーム描くと
 * GPUプロセスのCPUが実測170〜300%まで上がり、割に合わなかった。
 * グリッチは滑らかさが要らないので、周期の大半を静止に使えるのが決定的な差。
 *
 * 画像が無いとき（ユーザー名由来のグラデーション）は効果を出さない。
 *
 * 【種類を足すときの条件】
 * - **ラスタライズは1回で終わらせる。** 動かすのは transform と opacity だけで、
 *   background や clip-path をアニメーションさせない
 * - `mix-blend-mode` を使わない（背景を読むので合成だけで完結できない）
 * - 連続で動かす場合は層を1枚に抑える。バーストで足りるならそちらを優先
 */

import { SubscriptionPlan } from '../utils/subscription';

export type BannerEffectKey =
  | 'none' | 'glitch'
  | 'sweep' | 'sweepAccent'
  | 'zoomIn' | 'zoomOut';

/** 効果の種類。key はプリセットの識別子、kind は実装の分岐 */
export type BannerEffectKind = 'glitch' | 'sweep' | 'zoom';

export interface GlitchParams {
  /** 1周期の秒数。乱れるのは2回のバーストの間だけで、残りは静止している */
  cycle: number;
  /** ずれ幅の倍率。1.0 で BANDS の既定値そのまま */
  shift: number;
}

export interface SweepParams {
  /** 1周期の秒数。光が通るのは cross の割合だけで、残りは画面外で待機 */
  cycle: number;
  /** 光が横切るのに使う周期の割合(%)。小さいほど速く走り、待ち時間が長くなる */
  cross: number;
  /** true でアクセント色、false で白 */
  tinted: boolean;
  /** 光の最大不透明度 */
  peak: number;
}

export interface ZoomParams {
  /** 1往復の秒数。寄って戻るまで */
  cycle: number;
  /** 最大倍率。1.0 を下回らせない（下回ると cover の外側が出て縁が見える） */
  scale: number;
  /**
   * 最初にどちらへ動くか。往復なので長く見れば同じだが、
   * 開いた直後の数十秒でどう見えるかが変わる（周期24秒に対して滞在は数十秒のため）。
   * in  = 等倍から寄っていく
   * out = 拡大した状態から引いていく
   */
  direction: 'in' | 'out';
}

export type BannerEffectParams = GlitchParams | SweepParams | ZoomParams;

export interface BannerEffectPreset {
  key: BannerEffectKey;
  kind?: BannerEffectKind;
  label: string;
  description: string;
  minPlan: number;
  params?: BannerEffectParams;
}

export const BANNER_EFFECTS: BannerEffectPreset[] = [
  { key: 'none', label: 'なし', description: '静止', minPlan: 0 },
  {
    key: 'glitch',
    kind: 'glitch',
    label: 'グリッチ',
    description: '数秒に一度、帯がずれて色が割れる',
    minPlan: SubscriptionPlan.PRO,
    params: { cycle: 6, shift: 1 } as GlitchParams,
  },
  // スイープは白とアクセント色の2種。色が画像と喧嘩するかは絵次第なので、
  // どちらが正解と決めきれない。**選ばせる側に倒してある。**
  //
  // ローディングのシマー（スケルトン）に見えないよう、
  // 周期を長く・帯を広く・不透明度を低くしてある。速くすると一気に安っぽくなる。
  {
    key: 'sweep',
    kind: 'sweep',
    label: 'スイープ（白）',
    description: '数秒に一度、白い光が斜めに横切る',
    minPlan: SubscriptionPlan.PRO,
    params: { cycle: 9, cross: 26, tinted: false, peak: 0.22 } as SweepParams,
  },
  {
    key: 'sweepAccent',
    kind: 'sweep',
    // アクセント色は白より沈むので peak を上げてある。同じ値にすると見えない
    label: 'スイープ（アクセント）',
    description: '数秒に一度、アクセント色の光が斜めに横切る',
    minPlan: SubscriptionPlan.PRO,
    params: { cycle: 9, cross: 26, tinted: true, peak: 0.26 } as SweepParams,
  },
  // ズームは向きだけが違う同じ往復。scale と cycle は揃えること
  // （片方だけ変えると「イン/アウト」ではなく別物の2つになる）
  {
    key: 'zoomIn',
    kind: 'zoom',
    label: 'ズームイン',
    description: 'ゆっくり寄っていく',
    minPlan: SubscriptionPlan.PRO,
    params: { cycle: 24, scale: 1.2, direction: 'in' } as ZoomParams,
  },
  {
    key: 'zoomOut',
    kind: 'zoom',
    label: 'ズームアウト',
    description: 'ゆっくり引いていく',
    minPlan: SubscriptionPlan.PRO,
    params: { cycle: 24, scale: 1.2, direction: 'out' } as ZoomParams,
  },
];

export const DEFAULT_BANNER_EFFECT: BannerEffectKey = 'none';

export const canUseBannerEffect = (key: string, plan: number): boolean =>
  plan >= (BANNER_EFFECTS.find(e => e.key === key)?.minPlan ?? 0);

/** 保存値を「実際に描画してよい key」に落とす。描画前に必ず通すこと */
export const resolveBannerEffect = (key: string | undefined, plan: number): BannerEffectKey => {
  const preset = BANNER_EFFECTS.find(e => e.key === key);
  return preset && plan >= preset.minPlan ? preset.key : DEFAULT_BANNER_EFFECT;
};

export const bannerEffectParams = (key: string): BannerEffectParams | undefined =>
  BANNER_EFFECTS.find(e => e.key === key)?.params;

export const bannerEffectKind = (key: string): BannerEffectKind | undefined =>
  BANNER_EFFECTS.find(e => e.key === key)?.kind;

/**
 * スローズームの sx。**下地の層にそのまま展開して使う。**
 * 別レイヤーを足さないので、追加コストは実質ゼロ。
 *
 * 1.0 を下回らせない。下回ると cover の外側が出て縁が見える。
 * in / out は往復の位相が逆なだけで、動き自体は同じ。
 *
 * 【拡大率を上げすぎないこと】
 * 拡大中はラスタライズ済みの絵を引き伸ばすので、倍率がそのまま解像感の劣化になる。
 * 現在の 1.2（20%拡大）は、元画像が表示サイズちょうどだと甘さが見える領域。
 * willChange で昇格させて解像度を確保させているが、限界はある。
 * 上げる場合はバナー画像の推奨解像度も一緒に見直すこと。
 */
export const bannerZoomSx = (p: ZoomParams) => {
  const name = `bannerZoom_${p.direction}`;
  const [rest, peak] = p.direction === 'in' ? ['scale(1)', `scale(${p.scale})`] : [`scale(${p.scale})`, 'scale(1)'];
  return {
    [`@keyframes ${name}`]: {
      '0%, 100%': { transform: rest },
      '50%': { transform: peak },
    },
    animation: `${name} ${p.cycle}s ease-in-out infinite`,
    willChange: 'transform',
    // 止めるときも拡大側で固定しない。等倍が素の状態
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      transform: 'scale(1)',
      willChange: 'auto',
    },
  };
};
